/**
 * Helper puro para resolver qué array de infraestructuras debe consumir la UI
 * del wizard y qué se considera «fila resuelta» a efectos de contadores.
 *
 * Contexto — OBS-FN-02-A:
 * El wizard mantiene dos arrays paralelos en dos `useState` independientes:
 *   - `normalizedResults` (hook `useNormalization`): filas tras normalizar
 *     coordenadas del ODT; solo contienen xFinal/yFinal las que venían con
 *     coords en el documento original. El resto quedan `status='pending'`.
 *   - `geocodedResults` (hook `useAutoGeocoding`): filas tras aplicar el
 *     orchestrator (CartoCiudad, CDAU, DERA, NGA, Catastro, cross-validation,
 *     centroide universal…). Contienen xFinal/yFinal para prácticamente todas
 *     las filas rescatadas por el cascade.
 *
 * Antes de este helper, `WizardPageV2` pintaba en la tabla y en la auditoría
 * Firestore (FB.08) el array `normalizedResults` directamente, descartando el
 * trabajo del orchestrator. Resultado visible: «6/60 geolocalizados» en un
 * documento donde el motor rescató 54 de 60. Este helper corrige el síntoma
 * pintando `geocodedResults` cuando existe y cayendo a las alternativas solo
 * cuando no hay nada del motor.
 *
 * Cascada de fuente:
 *   1. `geocodedResults` — el cascade produjo algo. Fuente principal tras el
 *      ciclo completo de P1.
 *   2. `normalizedResults` — solo ejecutada la normalización (estado
 *      transitorio entre la carga y el fin del geocoding; también el caso
 *      «ODT con todas las coords y orchestrator no tiene nada que añadir»).
 *   3. `extractionData.infrastructures` — raw sin score; solo como último
 *      recurso si el usuario navega a P2 antes de que la normalización
 *      termine. Requiere cast porque `ExtractedInfrastructure` carece de
 *      `xFinal/yFinal/score`. El cast es **unsafe** y está marcado como tal.
 *
 * Complemento — `isResolvedStatus`:
 * Las filas cuyo `status` vale 'geocoded' | 'manual' | 'original' tienen
 * `xFinal/yFinal` válidos aunque el score varíe. 'manual' aparece en casos
 * `REVISION_MANUAL` del CrossValidator (>500 m de discrepancia): las coords
 * son las del documento, el motor avisa sin sobrescribir. 'original' es el
 * caso `VALIDADO` (<25 m). 'geocoded' cubre el resto. 'pending' y 'failed'
 * no tienen coords resueltas.
 *
 * @module utils/pickInfrasForDisplay
 */

import type { ProcessedInfrastructure, CoordinateStatus } from '../types/processing'

/**
 * Subconjunto mínimo de `ExtractionResult` que este helper necesita consumir.
 * Evita acoplar el módulo a la interfaz completa de `components/Step1.tsx`,
 * que arrastra File, FormatAnalysis, etc.
 */
export interface ExtractionFallback {
  infrastructures: unknown[]
}

/**
 * Decide qué array de infraestructuras debe pintar la UI del wizard.
 *
 * Aplica cascada `geocodedResults > normalizedResults > extractionData` y
 * devuelve el array no vacío de mayor precedencia, junto con una etiqueta
 * que identifica de qué fuente proviene. Útil para auditoría y logging.
 *
 * Nota: el caller es responsable de mantener el orden de precedencia
 * coherente con el ciclo de vida del wizard. Este helper no muta nada.
 *
 * @param normalizedResults  Salida del hook `useNormalization`.
 * @param geocodedResults    Salida del hook `useAutoGeocoding`.
 * @param extractionData     `ExtractionResult` del paso de extracción.
 * @returns Array a pintar + fuente efectiva. Array vacío si nada está listo.
 */
export function pickInfrasForDisplay(
  normalizedResults: ProcessedInfrastructure[],
  geocodedResults: ProcessedInfrastructure[],
  extractionData: ExtractionFallback | null | undefined,
): { data: ProcessedInfrastructure[]; source: 'geocoded' | 'normalized' | 'raw' | 'empty' } {
  if (geocodedResults.length > 0) {
    return { data: geocodedResults, source: 'geocoded' }
  }
  if (normalizedResults.length > 0) {
    return { data: normalizedResults, source: 'normalized' }
  }
  if (extractionData && extractionData.infrastructures.length > 0) {
    // Último recurso: raw sin score. Estado transitorio antes de que termine
    // la normalización. El cast es intencionadamente inseguro — las filas
    // carecen de xFinal/yFinal/score y el caller debe tratarlas como tales
    // (los contadores de «filas resueltas» darán 0, que es lo correcto).
    return {
      data: extractionData.infrastructures as unknown as ProcessedInfrastructure[],
      source: 'raw',
    }
  }
  return { data: [], source: 'empty' }
}

/**
 * P1PROV-a (DOP-3 a): lo que la geolocalización lleva hecho mientras corre.
 * Lo publica `useAutoGeocoding` fila a fila y lo vacía al terminar, al fallar
 * o al descartar la tanda. NO es `results`: once lectores de `WizardPageV2`
 * toman «`geocodedResults` no vacío» como «la geolocalización terminó» (P2,
 * la flecha, `processedData`…), así que los resultados a medias viven aparte
 * y solo los mezcla la tabla de P1 (`pickInfrasForP1`).
 */
export interface GeolocalizacionParcial {
  /** Ids de las filas que entran en la pasada; las demás salen ya con su valor. */
  enPasada: ReadonlySet<string>
  /** Filas de la pasada ya terminadas, con su resultado, por id. */
  terminadas: ReadonlyMap<string, ProcessedInfrastructure>
}

const SIN_PENDIENTES: ReadonlySet<string> = new Set<string>()

/**
 * P1PROV-a (DOP-1 a): fuente de la tabla de P1. Igual que `pickInfrasForDisplay`
 * y, mientras se geolocaliza (`parcial` no nulo y aún sin resultados finales),
 * cada fila terminada sale con su resultado y las que faltan se devuelven tal
 * cual y se marcan en `pendientes` (ids, explícito: no se deduce de la nota).
 * Las filas que no entran en la pasada no son pendientes: salen con su valor,
 * como en el resultado final del *hook*. Orden y longitud no cambian.
 */
export function pickInfrasForP1(
  normalizedResults: ProcessedInfrastructure[],
  geocodedResults: ProcessedInfrastructure[],
  extractionData: ExtractionFallback | null | undefined,
  parcial: GeolocalizacionParcial | null | undefined,
): {
  data: ProcessedInfrastructure[]
  source: 'geocoded' | 'normalized' | 'raw' | 'empty'
  pendientes: ReadonlySet<string>
} {
  const base = pickInfrasForDisplay(normalizedResults, geocodedResults, extractionData)
  if (!parcial || base.source !== 'normalized') return { ...base, pendientes: SIN_PENDIENTES }
  const pendientes = new Set<string>()
  const data = base.data.map(fila => {
    const terminada = parcial.terminadas.get(fila.id)
    if (terminada) return terminada
    if (parcial.enPasada.has(fila.id)) pendientes.add(fila.id)
    return fila
  })
  return { data, source: base.source, pendientes }
}

/**
 * Conjunto de estados que indican «fila con coordenadas finales utilizables».
 *
 * Incluye:
 *   - `original`: Caso A.1 VALIDADO del CrossValidator (doc confirmado, <25 m).
 *   - `geocoded`: Caso A.2 REVISION_MENOR, A.3 USAR_REFERENCIA, B
 *                  GEOCODIFICADO, B BAJA_CONFIANZA.
 *   - `manual`:   Caso A.4 REVISION_MANUAL (>500 m). Coords del documento
 *                  conservadas; el motor alerta sin sobrescribir.
 *
 * Excluye `pending` (sin coords) y `failed` (geocoding erróneo). Esta
 * semántica — «filas con coord final, independientemente del score» — se
 * usa para reportar cobertura al usuario y en auditoría Firestore FB.08.
 */
export const RESOLVED_STATUSES: ReadonlySet<CoordinateStatus> = new Set<CoordinateStatus>([
  'original',
  'geocoded',
  'manual',
])

/**
 * Predicado: ¿tiene la infraestructura coordenadas finales utilizables?
 *
 * Dos condiciones simultáneas:
 *   1. `status` ∈ {original, geocoded, manual} — fila resuelta por el
 *      pipeline (no descartada, no pendiente).
 *   2. `xFinal` e `yFinal` son números finitos — cinturón y tirantes frente a
 *      inconsistencias residuales del hook en casos degenerados.
 *
 * Este predicado sustituye al antiguo `filter(r => r.xFinal && r.yFinal &&
 * r.score > 0)` que excluía incorrectamente las filas `manual` (score 30).
 */
export function isResolved(infra: Pick<ProcessedInfrastructure, 'status' | 'xFinal' | 'yFinal'>): boolean {
  if (!RESOLVED_STATUSES.has(infra.status)) return false
  const { xFinal, yFinal } = infra
  return typeof xFinal === 'number' && Number.isFinite(xFinal)
    && typeof yFinal === 'number' && Number.isFinite(yFinal)
}

/**
 * Conveniencia: cuenta filas resueltas en un array de infraestructuras.
 */
export function countResolved(infras: ProcessedInfrastructure[]): number {
  let n = 0
  for (const i of infras) if (isResolved(i)) n++
  return n
}
